import { isPlatformBrowser, DOCUMENT, CommonModule } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import gsap from 'gsap';
import Draggable from 'gsap/Draggable';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { environment } from '../../../environments/environment';
import { FrontPageModel } from '../../models/home.model';
import { MediaModel } from '../../models/media.model';
import { SearchBoxContentModel } from '../../models/search-box.model';
import { AppService } from '../../services/app.service';
import { MetaService } from '../../services/meta.service';
import { SettingsService } from '../../services/settings.service';
import { Router, RouterModule } from '@angular/router';
import { SearchboxComponent } from '../../parts/search-box/search-box.component';
import { ApiImagePipe } from '../../pipes/image.pipe';
import { InnerHtmlComponent } from '../../parts/inner-html/inner-html.component';
import { ImageSizePipe } from '../../pipes/image-size.pipe';
import { PropertyListComponent } from '../../parts/property-list/property-list.component';
import { TranslationPipe } from '../../pipes/translation.pipe';
import { DateAdapter } from '@angular/material/core';
import { FaqListComponent } from '../../parts/faq-list/faq-list.component';
import { SettingsModel } from '../../models/setting.model';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  imports: [
    SearchboxComponent,
    ApiImagePipe,
    InnerHtmlComponent,
    RouterModule,
    ImageSizePipe,
    PropertyListComponent,
    TranslationPipe,
    FaqListComponent,
    CommonModule,
  ],
  standalone: true,
})
export class HomeComponent implements OnInit {
  data: FrontPageModel = new FrontPageModel();
  apiDomain = environment.apiDomain;
  currentSearchBoxData = new SearchBoxContentModel();

  isMobile = false;
  media: MediaModel = new MediaModel();

  settings = new SettingsModel();

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private appService: AppService,
    private metaService: MetaService,
    private settingsService: SettingsService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnInit(): void {
    this.settings = this.settingsService.settings();

    this.isMobile = this.settingsService.getMobileStatus();

    switch (this.settings.ClientId) {
      case 1:
        this.currentSearchBoxData.displayName = 'Stockholm';
        this.currentSearchBoxData.url = 'Stockholm,Sverige-c9880';
        break;
      case 2:
        this.currentSearchBoxData.displayName = 'Oslo';
        this.currentSearchBoxData.url = 'Oslo,Norge-c29309';
    }

    this.appService.getData('frontpage').then((x) => {
      this.data = JSON.parse(x);

      this.setMeta();

      if (this.data && this.data.Media) {
        this.media = this.isMobile
          ? (this.data.MediaMobile ?? this.data.Media ?? new MediaModel())
          : (this.data.Media ?? new MediaModel());
      }

      if (isPlatformBrowser(this.platformId)) {
        gsap.registerPlugin(ScrollTrigger, Draggable);

        const scrollTrigger = this.initScrollTriggers;
        var checkExist = setInterval(function () {
          if (document.getElementById('section-rendered')) {
            clearInterval(checkExist);
            scrollTrigger();
          }
        }, 100);
      }
    });
  }

  initScrollTriggers() {
    const topVideo = document.querySelector<HTMLVideoElement>('#top-video');
    if (topVideo) {
      topVideo.muted = true;
      topVideo.play();
    }

    document.querySelectorAll('.destination-content').forEach((box) => {
      const scrollBox = gsap.timeline({
        scrollTrigger: {
          trigger: box,
          pin: false,
          start: '70px bottom',
          end: 'bottom top',
          toggleActions: 'play none none reverse',
        },
      });

      const parentElement = box.parentElement;
      if (parentElement && parentElement.classList.contains('left')) {
        scrollBox.from(box, { y: 80, x: -100, opacity: 0, rotate: 20 });
      } else if (parentElement && parentElement.classList.contains('right')) {
        scrollBox.from(box, { y: 80, x: 100, opacity: 0, rotate: -20 });
      } else {
        scrollBox.from(box, { y: 80, opacity: 0 });
      }
    });

    const tl = gsap.timeline();
    tl.from('.text > *', 1.8, {
      opacity: 1,
      ease: 'power4.out',
      delay: 1,
      stagger: {
        amount: 0.3,
      },
    });

    if (topVideo) {
      gsap.to('#top-video', {
        scale: 1.3,
        scrollTrigger: {
          trigger: '#hero',
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
      });
    }
  }

  setMeta() {
    if (this.data) {
      this.metaService.setMeta(
        this.data.Title ?? '',
        this.data.MetaDescription ?? '',
        this.data.MediaMobile ?? new MediaModel(),
        '/',
      );
      if (this.router.url.includes('?')) {
        this.metaService.setCanonical(
          this.data.ClientId,
          this.router.url.split('?')[0],
        );
      }
    }
  }

  getLocationColumnCount(numberOfLocations: number): number {
    switch (numberOfLocations) {
      case 8:
        return 4;
      case 7:
        return 4;
      case 6:
        return 3;
      case 5:
        return 3;
      case 4:
        return 2;
      case 3:
        return 3;
      case 2:
        return 2;
      default:
        return 4;
    }
  }
}
